import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { cn } from '../../utils/cn';
import { useControllableState } from '../../hooks/useControllableState';
import { Keys } from '../../utils/keys';
import { Icon, type IconName } from '../../icons/Icon';
import { useMergedRefs } from '../Tooltip/mergeRefs';
import {
  optionBase,
  optionGroupLabel,
  triggerBase,
  triggerInline,
  triggerInlineSizes,
  triggerSizes,
  useFieldA11y,
  type FieldControlProps,
} from '../shared/fieldStyles';
import { ListboxPopup } from '../shared/ListboxPopup';
import { useListboxNavigation } from '../shared/useListboxNavigation';
import { useTypeahead } from '../shared/useTypeahead';
import { useDsT } from '../../services/i18n';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Options sharing a group label render under a sticky group heading. */
  group?: string;
  /** Secondary line rendered under the label in the option row. */
  description?: string;
  /** Leading icon node. Not JSON-serialisable — Studio metadata uses `iconName`. */
  icon?: ReactNode;
  /** Leading icon by name. The serialisable form of `icon`, used by descriptors. */
  iconName?: IconName;
  /**
   * Short trailing hint aligned to the right of the row — a unit code, a count, a currency. Unlike
   * `description` it stays on the same line, so a two-token option ("Kilogram · KG") stays one row high.
   */
  hint?: string;
}

/**
 * The element the `ref` resolves to.
 *
 * The rebuilt control is a button-triggered listbox, so at runtime the ref always receives the
 * trigger `<button>`. `HTMLSelectElement` stays in the union purely so refs declared against the
 * old native implementation (`useRef<HTMLSelectElement>(null)`) keep type-checking.
 */
export type SelectTriggerElement = HTMLButtonElement | HTMLSelectElement;

export type SelectAppearance = 'field' | 'inline';

export interface SelectProps extends FieldControlProps {
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /**
   * @deprecated Use `onValueChange`. Kept for source compatibility with the native implementation;
   * it is called with a minimal `{ target: { value } }`-shaped object, not a real DOM event.
   */
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  /** Shown on the trigger while no value is selected. */
  placeholder?: string;
  /**
   * Trigger chrome. `field` is the standalone control with its own border and height; `inline` is a bare,
   * auto-width trigger for use *inside* another field's frame (the UOM picker in `QuantityInput`), where a
   * second border would be a box inside a box.
   */
  appearance?: SelectAppearance;
  /** Overrides what the trigger shows for the current selection (default: the option's `label`). */
  renderValue?: (option: SelectOption | undefined) => ReactNode;
  /** Adds a row that clears the selection back to `''`. */
  allowEmpty?: boolean;
  fullWidth?: boolean;
  /** Message shown when there is nothing to choose from. */
  emptyMessage?: string;
  required?: boolean;
  autoFocus?: boolean;
  className?: string;
  'aria-labelledby'?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  onFocus?: (event: FocusEvent<HTMLButtonElement>) => void;
  onBlur?: (event: FocusEvent<HTMLButtonElement>) => void;
}

interface OptionGroup {
  label: string;
  options: SelectOption[];
}

interface FlatOptions {
  /** Every row in render order; the navigation index is the position in this array. */
  flat: SelectOption[];
  indexOf: Map<SelectOption, number>;
  clearRow: SelectOption | null;
  ungrouped: SelectOption[];
  groups: OptionGroup[];
}

function flattenOptions(options: SelectOption[], allowEmpty: boolean | undefined, placeholder: string | undefined, clearText = 'Clear'): FlatOptions {
  const ungrouped: SelectOption[] = [];
  const byGroup = new Map<string, SelectOption[]>();
  for (const o of options) {
    if (!o.group) {
      ungrouped.push(o);
      continue;
    }
    const list = byGroup.get(o.group) ?? [];
    list.push(o);
    byGroup.set(o.group, list);
  }
  const groups: OptionGroup[] = Array.from(byGroup, ([label, opts]) => ({ label, options: opts }));
  const clearRow: SelectOption | null = allowEmpty ? { value: '', label: placeholder ?? clearText } : null;
  const flat = [...(clearRow ? [clearRow] : []), ...ungrouped, ...groups.flatMap((g) => g.options)];
  const indexOf = new Map(flat.map((o, i) => [o, i] as const));
  return { flat, indexOf, clearRow, ungrouped, groups };
}

function OptionIcon({ option }: { option: SelectOption }) {
  if (option.icon != null) return <span className="inline-flex shrink-0 items-center text-fg-muted">{option.icon}</span>;
  if (option.iconName) return <Icon name={option.iconName} size={14} className="shrink-0 text-fg-muted" />;
  return null;
}

/**
 * Single-select listbox (WAI-ARIA 1.2 combobox + listbox popup).
 *
 * There is deliberately **no `<select>` element**: the trigger is a real button carrying the
 * selection, and the options live in a portalled, collision-aware, animated panel. A hidden input
 * carries `name` so the control still submits inside a plain form.
 */
export const Select = forwardRef<SelectTriggerElement, SelectProps>(function Select(
  {
    options,
    value,
    defaultValue,
    onChange,
    onValueChange,
    placeholder,
    appearance = 'field',
    renderValue,
    allowEmpty,
    fullWidth = true,
    emptyMessage: emptyMessageProp,
    className,
    id,
    name,
    size = 'md',
    invalid,
    readOnly,
    disabled,
    required,
    autoFocus,
    onKeyDown,
    onFocus,
    onBlur,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
  },
  ref,
) {
  const reactId = useId();
  const t = useDsT();
  const emptyMessage = emptyMessageProp ?? t('ds.select.empty', 'No options');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const setTrigger = useMergedRefs<HTMLButtonElement>(triggerRef, ref as Ref<HTMLButtonElement>);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useControllableState<string>({
    value,
    defaultValue: defaultValue ?? '',
    onChange: onValueChange,
  });
  const a11y = useFieldA11y({ invalid, 'aria-describedby': ariaDescribedBy });
  const inert = disabled || readOnly;

  const { flat, indexOf, clearRow, ungrouped, groups } = useMemo(
    () => flattenOptions(options, allowEmpty, placeholder, t('ds.common.clear', 'Clear')),
    [options, allowEmpty, placeholder, t],
  );
  // Read through a ref so type-ahead and the open effect never depend on the (often inline) options array.
  const flatRef = useRef(flat);
  flatRef.current = flat;
  const currentRef = useRef(current);
  currentRef.current = current;

  const selectedOption = current === '' ? undefined : options.find((o) => o.value === current);

  const closeAndFocus = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);
  const openList = useCallback(() => {
    if (!inert) setOpen(true);
  }, [inert]);

  const commit = useCallback(
    (next: string) => {
      if (inert) return;
      setCurrent(next);
      onChange?.({ target: { value: next } } as unknown as ChangeEvent<HTMLSelectElement>);
      closeAndFocus();
    },
    [inert, setCurrent, onChange, closeAndFocus],
  );

  const selectIndex = useCallback(
    (i: number) => {
      const option = flatRef.current[i];
      if (!option || option.disabled) return;
      commit(option.value);
    },
    [commit],
  );

  const nav = useListboxNavigation({
    count: flat.length,
    open,
    isDisabled: (i) => !!flat[i]?.disabled,
    onSelect: selectIndex,
    onOpen: openList,
    onClose: closeAndFocus,
  });
  const { reset: navReset, setActiveIndex } = nav;

  // On open, highlight the selected row (or the first enabled one). Deliberately keyed on `open`
  // only: the options array is usually rebuilt on every parent render.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      const idx = flatRef.current.findIndex((o) => o.value === currentRef.current);
      navReset(idx >= 0 ? idx : 0);
    }
    wasOpen.current = open;
  }, [open, navReset]);

  const typeahead = useTypeahead({
    labels: () => flatRef.current.map((o) => o.label),
    onMatch: (i) => {
      if (open) setActiveIndex(i);
      else {
        // Closed behaves like a native select: the match is chosen outright.
        const option = flatRef.current[i];
        if (option) commit(option.value);
      }
    },
    from: () => (open ? nav.activeIndex : flatRef.current.findIndex((o) => o.value === currentRef.current)),
    isDisabled: (i) => !!flatRef.current[i]?.disabled,
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || inert) return;
    if (nav.handleKeyDown(event)) return;
    if (event.key === Keys.Space) {
      event.preventDefault();
      if (!open) openList();
      else if (nav.activeIndex >= 0) selectIndex(nav.activeIndex);
      return;
    }
    if (event.key === Keys.Enter && !open) {
      event.preventDefault();
      openList();
      return;
    }
    if (event.key.length === 1 && typeahead(event.key)) event.preventDefault();
  };

  const inline = appearance === 'inline';
  const groupId = (index: number) => `${reactId}-group-${index}`;

  const renderRow = (option: SelectOption) => {
    const index = indexOf.get(option) ?? -1;
    const isSelected = option.value === current;
    const active = nav.activeIndex === index;
    return (
      // The listbox is driven from the trigger via aria-activedescendant, so the rows carry no key handlers.
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <li
        key={option === clearRow ? '__zo-empty__' : option.value}
        id={nav.optionId(index)}
        role="option"
        aria-selected={isSelected}
        aria-disabled={option.disabled || undefined}
        data-active={active || undefined}
        onMouseMove={() => {
          if (!active) setActiveIndex(index);
        }}
        onClick={() => {
          if (!option.disabled) commit(option.value);
        }}
        className={cn(optionBase, option.description && 'items-start py-2', option === clearRow && 'text-fg-muted')}
      >
        <OptionIcon option={option} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate">{option.label}</span>
          {option.description ? <span className="truncate text-sm font-normal text-fg-subtle">{option.description}</span> : null}
        </span>
        {option.hint ? <span className="code shrink-0 text-xs uppercase text-fg-subtle">{option.hint}</span> : null}
        {isSelected ? <Icon name="check" size={14} className="ms-auto shrink-0 text-accent" /> : null}
      </li>
    );
  };

  return (
    <span className={cn('zo-select relative inline-flex', inline ? 'h-full w-auto' : fullWidth ? 'w-full' : 'w-auto')} data-size={size} data-appearance={appearance}>
      <button
        ref={setTrigger}
        id={id}
        type="button"
        role="combobox"
        autoFocus={autoFocus}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open && flat.length > 0 ? nav.listboxId : undefined}
        aria-activedescendant={nav.activeDescendant}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-required={required || undefined}
        aria-readonly={readOnly || undefined}
        disabled={disabled}
        data-invalid={invalid || undefined}
        data-readonly={readOnly || undefined}
        data-placeholder={!selectedOption || undefined}
        data-open={open || undefined}
        onClick={() => (open ? closeAndFocus() : openList())}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onBlur={(event) => {
          if (open) setOpen(false);
          onBlur?.(event);
        }}
        className={cn(inline ? triggerInline : triggerBase, inline ? triggerInlineSizes[size] : triggerSizes[size], className)}
        {...a11y}
      >
        {selectedOption ? <OptionIcon option={selectedOption} /> : null}
        <span className={cn('min-w-0 truncate', !inline && 'flex-1', !selectedOption && !renderValue && 'text-fg-subtle')}>
          {renderValue ? renderValue(selectedOption) : selectedOption ? selectedOption.label : (placeholder ?? '')}
        </span>
        <Icon
          name="chevron-down"
          size={inline ? 11 : 14}
          aria-hidden
          className={cn('shrink-0 transition-transform motion-fast', inline ? 'text-fg-subtle' : 'text-fg-muted', open && 'rotate-180')}
        />
      </button>
      {name ? <input type="hidden" name={name} value={current} /> : null}
      <ListboxPopup
        anchorRef={triggerRef}
        open={open}
        matchWidth={!inline}
        placement={inline ? 'bottom-end' : 'bottom-start'}
        listboxId={nav.listboxId}
        optionCount={flat.length}
        message={flat.length === 0 ? emptyMessage : null}
        onClose={closeAndFocus}
        aria-label={ariaLabel ?? (ariaLabelledBy ? undefined : t('ds.common.options', 'Options'))}
        aria-labelledby={ariaLabelledBy}
      >
        {clearRow ? renderRow(clearRow) : null}
        {ungrouped.map(renderRow)}
        {groups.map((group, gi) => (
          <li key={group.label} role="presentation">
            <div id={groupId(gi)} className={optionGroupLabel}>
              {group.label}
            </div>
            <ul role="group" aria-labelledby={groupId(gi)} className="m-0 list-none p-0">
              {group.options.map(renderRow)}
            </ul>
          </li>
        ))}
      </ListboxPopup>
    </span>
  );
});
